import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { LeadContext } from "@/types";

/**
 * Collects CRM-ready lead metadata from the current environment.
 *
 * Reads originating-page hints from the URL query (passed by detail-page CTAs),
 * plus UTM params, referrer, user agent and a client timestamp. The result is
 * attached to every lead submission so future CRM sync needs no schema change.
 */
export function useLeadContext(): {
  context: LeadContext;
  sourceLabel?: string;
} {
  const { search, pathname } = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(search);

    const utm: LeadContext["utm"] = {
      source: params.get("utm_source") ?? undefined,
      medium: params.get("utm_medium") ?? undefined,
      campaign: params.get("utm_campaign") ?? undefined,
      term: params.get("utm_term") ?? undefined,
      content: params.get("utm_content") ?? undefined,
    };
    const hasUtm = Object.values(utm).some(Boolean);

    const sourceType = params.get("src_type") ?? undefined;
    const sourceSlug = params.get("src_slug") ?? undefined;
    const sourceLabel = params.get("src_label") ?? undefined;

    const context: LeadContext = {
      source_page: `${pathname}${search}`,
      source_type: sourceType,
      source_label: sourceLabel,
      industry: sourceType === "industry" ? sourceSlug : undefined,
      service: sourceType === "service" ? sourceSlug : undefined,
      solution: sourceType === "solution" ? sourceSlug : undefined,
      referrer:
        typeof document !== "undefined" && document.referrer
          ? document.referrer
          : undefined,
      utm: hasUtm ? utm : undefined,
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      client_timestamp: new Date().toISOString(),
    };

    return { context, sourceLabel };
  }, [search, pathname]);
}
